import { useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { userPaths } from 'routes/constants';

import { useLocation } from 'react-router-dom';

import { refreshEndUserDashboardState } from 'services/endUser/refreshEndUserDashboardState';
import { resolveEndUserAuth } from 'services/endUser/resolveEndUserAuth';

import type { RootState } from 'store/store';

export const isMyAccountPath = (pathname: string): boolean =>
  pathname === userPaths.myAccount || pathname.startsWith(`${userPaths.myAccount}/`);

/**
 * Refreshes GET /eu/{euId}/state when navigating away from My Account so profile
 * updates (e.g. preferred name) reflect on the main end-user screens.
 */
export function useRefreshEndUserStateOnMyAccountExit(): void {
  const location = useLocation();
  const dispatch = useDispatch();
  const euId = useSelector((state: RootState) => state.auth.euId);
  const idToken = useSelector((state: RootState) => state.userSession.idToken);
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;
    previousPathRef.current = currentPath;

    if (!isMyAccountPath(previousPath) || isMyAccountPath(currentPath)) {
      return;
    }

    const auth = resolveEndUserAuth({ euId, idToken });
    if (!auth.euId || !auth.idToken) {
      return;
    }

    void refreshEndUserDashboardState(auth.euId, auth.idToken, dispatch);
  }, [dispatch, euId, idToken, location.pathname]);
}
